import type { BarChartSeries, LineChartSeries } from '@mantine/charts'
import { BarChart, LineChart } from '@mantine/charts'
import { ActionIcon, Button, Center, Group, Paper, Stack, Tabs, Text, Title } from '@mantine/core'
import {
  IconChevronLeft,
  IconChevronRight,
  IconEye,
  IconEyeOff,
  IconHexagons,
  IconMessage,
  IconMessageCirclePlus,
  IconMessages,
  IconSend,
  IconTrendingUp,
  IconUser,
} from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'

import { colors } from '@/configs/colors'
import { useGlobalState } from '@/hooks/useGlobalState'
import {
  canNavigateToPreviousPeriod,
  eachDateStringInclusive,
  formatChartDate,
  getExplicitDateRange,
} from '@/utils/analytics'

import CMConversationBarChart from './CMConversationBarChart'
import { StatInfoGraphic } from './StatInfoGraphic'
import TabChartWrapper from './TabChartWrapper'

const TabKeys = {
  activeUsersLineChart: 'activeUsersLineChart',
  messagesSentBarChart: 'messagesSentBarChart',
  cmConversationBarChart: 'cmConversationBarChart',
} as const

const messagesSentSeries: BarChartSeries[] = [
  {
    name: 'messages',
    label: 'Daily Messages',
    color: colors.chartColors[0],
  },
]

const activeMembersSeries: LineChartSeries[] = [
  {
    name: 'total',
    label: 'Total Active Users',
    color: colors.chartColors[0],
  },
]

export default function InsightsCharts() {
  const { api } = useGlobalState()

  const [activeTab, setActiveTab] = useState<string | null>(TabKeys.activeUsersLineChart)
  const [showNonPublishedCMs, setShowNonPublishedCMs] = useState(false)
  const [backPeriod, setBackPeriod] = useState(0)

  const { data: sponsorInfo } = useQuery({
    queryKey: ['sponsor', 'info'],
    queryFn: () => api.getSponsorInfo(),
    staleTime: 5 * 60 * 1000,
  })

  const dateRange = useMemo(
    () => getExplicitDateRange(backPeriod, sponsorInfo?.createdAt),
    [backPeriod, sponsorInfo?.createdAt],
  )

  const {
    data: activeMembersData,
    isLoading: activeMembersLoading,
    error: activeMembersError,
  } = useQuery({
    queryKey: ['analytics', 'activeMembersByDate', backPeriod, dateRange.startDate],
    queryFn: () => api.getActiveMembersByDate(dateRange.analyticsParams),
  })

  // Only the current period (backPeriod === 0) ends on today, which is still
  // in progress in PostHog — drop it so we don't chart a partial count.
  const isCurrentPeriod = backPeriod === 0

  const activeMembersChartData = useMemo(() => {
    const raw = activeMembersData?.data ?? []
    if (isCurrentPeriod && raw.length > 0) {
      return raw.slice(0, -1)
    }
    return raw
  }, [activeMembersData, isCurrentPeriod])

  const {
    data: allConversationsPerCmData,
    isLoading: allConversationsPerCmLoading,
    error: allConversationsPerCmError,
  } = useQuery({
    queryKey: ['analytics', 'allConversationsPerCm', backPeriod, dateRange.startDate],
    queryFn: () => api.getAllConversationsPerCm(dateRange.analyticsParams),
  })

  const {
    data: contentModulesData,
    isLoading: _contentModulesLoading,
    error: _contentModulesError,
  } = useQuery({
    queryKey: ['cm', 'list'],
    queryFn: () => api.getContentModules({ exclude_removed: false }),
    initialData: [],
  })

  const {
    data: billingCreditsData,
    isLoading: billingCreditsLoading,
    error: billingCreditsError,
  } = useQuery({
    queryKey: ['billing', 'credits', backPeriod, dateRange.startDate],
    queryFn: () =>
      api.getBillingCreditsData({
        start_date: dateRange.billingParams.startDate,
        end_date: dateRange.billingParams.endDate,
      }),
  })

  const messagesSentChartData = useMemo(() => {
    const credits = billingCreditsData?.credits ?? []
    if (credits.length === 0) return []

    // Key on the UTC day the server already bucketed the row into. Parsing d.date (a naive
    // timestamp) into a Date first reads it as local and shifts the day at UTC+ offsets.
    const dataMap = new Map(credits.map(d => [d.date.split('T')[0], d.activeCredits]))

    return eachDateStringInclusive(dateRange.startDate, dateRange.endDate).map(date => ({
      date,
      messages: dataMap.get(date) ?? 0,
    }))
  }, [billingCreditsData, dateRange.startDate, dateRange.endDate])

  const handlePreviousPeriod = () => {
    setBackPeriod(prev => prev - 1)
  }

  const handleNextPeriod = () => {
    setBackPeriod(prev => prev + 1)
  }

  const canGoPrevious = canNavigateToPreviousPeriod(backPeriod, sponsorInfo?.createdAt)
  const canGoNext = backPeriod < 0

  const maxDailyMessages = useMemo(() => {
    return messagesSentChartData.length > 0 ? Math.max(...messagesSentChartData.map(d => d.messages)) : 0
  }, [messagesSentChartData])

  // Process chart data to replace "Unknown" labels with actual content module titles
  const processedChartData = useMemo(() => {
    if (!allConversationsPerCmData?.data || !contentModulesData?.length) {
      return allConversationsPerCmData?.data || []
    }

    return allConversationsPerCmData.data
      .map(entry => {
        if (entry.label === 'Unknown') {
          const matchingContentModule = contentModulesData.find(cm => cm.taskId === entry.taskId)
          if (matchingContentModule) {
            console.log('[InsightsCharts] 🎯 Unknown label Matched and Updated', {
              unknownEntry: entry,
              matchingContentModule,
            })
            return {
              ...entry,
              label: matchingContentModule.title || matchingContentModule.name || 'Unknown',
            }
          }
          // Keep sponsor entries with special null taskId even if no match is found
          if (entry.taskId === null || entry.taskId === '$$_posthog_breakdown_null_$$') {
            return {
              ...entry,
              label: entry.label || 'Sponsor',
            }
          }
          // Log when no match is found and return null for filtering
          console.warn('[InsightsCharts] ❌ No match found for unknown entry:', entry)
          return null
        }
        return entry
      })
      .filter(entry => entry !== null)
  }, [allConversationsPerCmData?.data, contentModulesData])

  const getAverageConversationLength = (): number => allConversationsPerCmData?.stats.averageConversationLength ?? 0

  // Filter data for published content modules
  const publishedChartData = useMemo(() => {
    if (!contentModulesData?.length) return processedChartData

    const publishedTaskIds = new Set(
      contentModulesData
        .filter(cm => cm.status === 'published')
        .map(cm => cm.taskId)
        .filter(Boolean),
    )

    return processedChartData.filter(
      entry =>
        publishedTaskIds.has(entry.taskId) || entry.taskId === null || entry.taskId === '$$_posthog_breakdown_null_$$',
    )
  }, [processedChartData, contentModulesData])

  // Filter data for non-published content modules
  const nonPublishedChartData = useMemo(() => {
    if (!contentModulesData?.length) return []

    const nonPublishedTaskIds = new Set(
      contentModulesData
        .filter(cm => cm.status !== 'published')
        .map(cm => cm.taskId)
        .filter(Boolean),
    )

    return processedChartData.filter(entry => nonPublishedTaskIds.has(entry.taskId))
  }, [processedChartData, contentModulesData])

  return (
    <Stack>
      <Paper radius="md" p="lg" shadow="sm" px="xl">
        <Group justify="space-between" mb="md" align="top">
          <Group gap="xl">
            <StatInfoGraphic
              icon={IconHexagons}
              label="Modules"
              value={processedChartData.length}
              tooltipText="Total number of Content Modules with conversations"
            />
            <StatInfoGraphic
              icon={IconMessages}
              label="Conversations"
              value={allConversationsPerCmData?.stats.totalConversations ?? 0}
              tooltipText="The total number of conversations for all content modules"
            />
            <StatInfoGraphic
              icon={IconSend}
              label="Messages Sent"
              value={billingCreditsData ? billingCreditsData.totalActiveCredits : '—'}
              tooltipText="Total messages sent during this 30-day period"
            />
            <StatInfoGraphic
              icon={IconTrendingUp}
              label="Avg Daily Messages"
              value={billingCreditsData ? billingCreditsData.averageCredits : '—'}
              tooltipText="Average messages sent per day"
            />
            <StatInfoGraphic
              icon={IconMessageCirclePlus}
              label="Unique Conversations"
              value={allConversationsPerCmData?.stats.totalUniqueConversations ?? 0}
              tooltipText="The total number of unique conversations"
            />
          </Group>

          <Group gap="xs" align="center">
            <ActionIcon
              variant="subtle"
              onClick={handlePreviousPeriod}
              disabled={!canGoPrevious}
              aria-label="Previous period">
              <IconChevronLeft size={20} />
            </ActionIcon>

            <Text size="sm" c="dimmed" style={{ minWidth: 180, textAlign: 'center' }}>
              {formatChartDate(dateRange.startDate, true)} — {formatChartDate(dateRange.endDate, true)}
            </Text>

            <ActionIcon variant="subtle" onClick={handleNextPeriod} disabled={!canGoNext} aria-label="Next period">
              <IconChevronRight size={20} />
            </ActionIcon>
          </Group>
        </Group>
      </Paper>

      <Paper radius="md" p="lg" shadow="sm" px="xl">
        <Tabs value={activeTab} onChange={setActiveTab}>
          <Tabs.List>
            <Tabs.Tab value={TabKeys.activeUsersLineChart} leftSection={<IconUser size={16} />}>
              Active End Users
            </Tabs.Tab>
            <Tabs.Tab value={TabKeys.messagesSentBarChart} leftSection={<IconSend size={16} />}>
              Messages Sent
            </Tabs.Tab>
            <Tabs.Tab value={TabKeys.cmConversationBarChart} leftSection={<IconMessage size={16} />}>
              CM Conversations
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value={TabKeys.activeUsersLineChart} py="md">
            <Title order={6} mb={isCurrentPeriod ? 4 : 'md'}>
              Daily Active End Users
            </Title>
            {isCurrentPeriod && (
              <Text size="xs" c="dimmed" mb="md">
                Today is excluded until it completes, since its count would be partial.
              </Text>
            )}
            <TabChartWrapper
              isActive={activeTab === TabKeys.activeUsersLineChart}
              isLoading={activeMembersLoading}
              error={activeMembersError}>
              <LineChart
                h={300}
                data={activeMembersChartData}
                dataKey="date"
                curveType="linear"
                series={activeMembersSeries}
                xAxisProps={{
                  tickFormatter: (date: string) => {
                    return formatChartDate(date)
                  },
                  angle: -45,
                  textAnchor: 'end',
                  interval: 0,
                }}
              />
            </TabChartWrapper>
          </Tabs.Panel>

          <Tabs.Panel value={TabKeys.messagesSentBarChart} py="md">
            <Title order={6} mb="md">
              Messages Sent per Day for All Users
            </Title>
            <TabChartWrapper
              isActive={activeTab === TabKeys.messagesSentBarChart}
              isLoading={billingCreditsLoading}
              error={billingCreditsError}>
              {messagesSentChartData.length === 0 ? (
                <Center h={300}>
                  <Text c="dimmed">No data available for this period</Text>
                </Center>
              ) : (
                <BarChart
                  h={300}
                  data={messagesSentChartData}
                  dataKey="date"
                  series={messagesSentSeries}
                  gridProps={{ horizontal: false, vertical: false }}
                  xAxisProps={{
                    tickFormatter: (date: string) => formatChartDate(date),
                    angle: -45,
                    textAnchor: 'end',
                    interval: 0,
                  }}
                  yAxisProps={{
                    label: { value: 'Messages', angle: -90, position: 'insideLeft' },
                    domain: [0, maxDailyMessages + 1],
                  }}
                  tooltipProps={{
                    labelFormatter: (label: string) => formatChartDate(label, true),
                    formatter: (value: number | null) => {
                      if (value === null) return null
                      return [`${value.toLocaleString()} messages`, 'Daily Messages']
                    },
                  }}
                />
              )}
            </TabChartWrapper>
          </Tabs.Panel>

          <Tabs.Panel value={TabKeys.cmConversationBarChart} py="md">
            <Group justify="space-between" align="center" mb="md">
              <Title order={6}>Conversations with each Content Module</Title>
              <Button
                variant="subtle"
                size="sm"
                leftSection={showNonPublishedCMs ? <IconEyeOff size={16} /> : <IconEye size={16} />}
                onClick={() => setShowNonPublishedCMs(!showNonPublishedCMs)}>
                {showNonPublishedCMs ? 'Hide' : 'Show'} Other CMs
              </Button>
            </Group>
            <TabChartWrapper
              isActive={activeTab === TabKeys.cmConversationBarChart}
              isLoading={allConversationsPerCmLoading}
              error={allConversationsPerCmError}>
              <Stack gap="xl">
                <CMConversationBarChart
                  conversationData={publishedChartData}
                  averageConversationLength={getAverageConversationLength()}
                  title="Published Content Modules"
                />
                {showNonPublishedCMs && (
                  <CMConversationBarChart
                    conversationData={nonPublishedChartData}
                    averageConversationLength={getAverageConversationLength()}
                    title="Other Content Modules (Draft, Testing, Pending Approval, Removed)"
                  />
                )}
              </Stack>
            </TabChartWrapper>
          </Tabs.Panel>
        </Tabs>
      </Paper>
    </Stack>
  )
}
